import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

const COOKIE_CONSENT_KEY = 'cookie-consent'
const CONSENT_TIMESTAMP_KEY = 'cookie-consent-timestamp'

// Cookie consent types
export type ConsentType = 'essential' | 'analytics' | 'marketing'

interface ConsentState {
  hasConsented: boolean
  preferences: Record<ConsentType, boolean>
  timestamp: number | null
}

export function getCookieConsent(): ConsentState {
  if (typeof window === 'undefined') {
    return { hasConsented: false, preferences: { essential: true, analytics: false, marketing: false }, timestamp: null }
  }

  const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
  const timestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY)

  if (!consent) {
    return { hasConsented: false, preferences: { essential: true, analytics: false, marketing: false }, timestamp: null }
  }

  try {
    return {
      hasConsented: true,
      preferences: JSON.parse(consent),
      timestamp: timestamp ? parseInt(timestamp, 10) : null
    }
  } catch {
    return { hasConsented: false, preferences: { essential: true, analytics: false, marketing: false }, timestamp: null }
  }
}

export function setCookieConsent(preferences: Record<ConsentType, boolean>): void {
  if (typeof window === 'undefined') return

  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences))
  localStorage.setItem(CONSENT_TIMESTAMP_KEY, Date.now().toString())
}

export function withdrawCookieConsent(): void {
  if (typeof window === 'undefined') return

  localStorage.removeItem(COOKIE_CONSENT_KEY)
  localStorage.removeItem(CONSENT_TIMESTAMP_KEY)
  // Clear any non-essential cookies
  clearNonEssentialCookies()
}

function clearNonEssentialCookies(): void {
  if (typeof window === 'undefined') return

  // Get all cookies
  const cookies = document.cookie.split(';')

  // Clear all cookies except essential ones
  const essentialCookies = ['i18nextLng'] // Keep language preference

  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim()
    if (!essentialCookies.includes(cookieName)) {
      // Clear cookie by setting expiration to past date
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    }
  })
}

interface CookieConsentProps {
  onConsent?: (preferences: Record<ConsentType, boolean>) => void
}

/**
 * CookieConsent - GDPR-compliant cookie consent banner
 *
 * Features:
 * - Blocks page access until consent is given
 * - Opt-in consent (no pre-checked boxes)
 * - Granular consent categories
 * - Persists consent in localStorage
 * - Compliant with German/EU GDPR requirements
 * - Withdraw consent option in settings
 */
export function CookieConsent({ onConsent }: CookieConsentProps) {
  const { t } = useTranslation()
  const [showBanner, setShowBanner] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<Record<ConsentType, boolean>>({
    essential: true, // Essential cookies are always enabled
    analytics: false,
    marketing: false
  })

  useEffect(() => {
    // Check if user has already consented
    const consentState = getCookieConsent()

    if (!consentState.hasConsented) {
      // Show banner if no consent
      setShowBanner(true)
    } else {
      // Notify parent of existing consent
      onConsent?.(consentState.preferences)
    }
  }, [onConsent])

  const handleAcceptAll = () => {
    const newPreferences = {
      essential: true,
      analytics: true,
      marketing: true
    }
    setPreferences(newPreferences)
    setCookieConsent(newPreferences)
    setShowBanner(false)
    onConsent?.(newPreferences)
  }

  const handleAcceptSelected = () => {
    setCookieConsent(preferences)
    setShowBanner(false)
    onConsent?.(preferences)
  }

  const handleTogglePreference = (type: ConsentType) => {
    if (type === 'essential') return // Essential cookies cannot be disabled

    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  return (
    <>
      {/* Overlay that blocks access to the page */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Cookie Consent Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[500px] sm:max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-2xl shadow-2xl z-[9999] border border-gray-200 dark:border-gray-800"
            role="dialog"
            aria-labelledby="cookie-title"
            aria-describedby="cookie-description"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 id="cookie-title" className="text-xl font-semibold text-gray-900 dark:text-white">
                    🍪 {t('cookies.title', 'Cookie & Privacy Consent')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('cookies.subtitle', 'We respect your privacy')}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showDetails ? t('cookies.showLess', 'Show less') : t('cookies.showMore', 'Show more')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Description */}
              <p id="cookie-description" className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {t('cookies.description',
                  'We use cookies and similar technologies to provide our services, personalize content, and analyze traffic. By using our website, you agree to our use of cookies.'
                )}
              </p>

              {/* Detailed Information (Expandable) */}
              {showDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 space-y-3"
                >
                  {/* Essential Cookies */}
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="essential"
                          checked={preferences.essential}
                          disabled
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-not-allowed opacity-50"
                        />
                        <label htmlFor="essential" className="font-medium text-gray-900 dark:text-white text-sm">
                          {t('cookies.essential', 'Essential Cookies')}
                        </label>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('cookies.alwaysActive', 'Always active')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
                      {t('cookies.essentialDescription',
                        'Required for the website to function properly. Includes session management and security.'
                      )}
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="analytics"
                        checked={preferences.analytics}
                        onChange={() => handleTogglePreference('analytics')}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <label htmlFor="analytics" className="font-medium text-gray-900 dark:text-white text-sm cursor-pointer">
                        {t('cookies.analytics', 'Analytics Cookies')}
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
                      {t('cookies.analyticsDescription',
                        'Help us understand how visitors interact with our website by collecting anonymous statistics.'
                      )}
                    </p>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="marketing"
                        checked={preferences.marketing}
                        onChange={() => handleTogglePreference('marketing')}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <label htmlFor="marketing" className="font-medium text-gray-900 dark:text-white text-sm cursor-pointer">
                        {t('cookies.marketing', 'Marketing Cookies')}
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
                      {t('cookies.marketingDescription',
                        'Used to track visitors across websites to display relevant advertisements.'
                      )}
                    </p>
                  </div>

                  {/* Links */}
                  <div className="text-xs text-gray-600 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <a
                      href={`/${localStorage.getItem('i18nextLng') || 'de'}/privacy`}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('cookies.privacyPolicy', 'Privacy Policy')}
                    </a>
                    {' • '}
                    <a
                      href={`/${localStorage.getItem('i18nextLng') || 'de'}/imprint`}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('cookies.imprint', 'Imprint')}
                    </a>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity font-medium text-sm"
                >
                  {t('cookies.acceptAll', 'Accept All')}
                </button>
                <button
                  onClick={handleAcceptSelected}
                  disabled={!showDetails}
                  className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('cookies.acceptSelected', 'Accept Selected')}
                </button>
              </div>

              {!showDetails && (
                <button
                  onClick={() => setShowDetails(true)}
                  className="w-full mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {t('cookies.customize', 'Customize preferences')}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

/**
 * CookieConsentSettings - Settings component to withdraw consent
 * This can be added to the Settings page
 */
export function CookieConsentSettings() {
  const { t } = useTranslation()
  const consentState = getCookieConsent()

  if (!consentState.hasConsented) {
    return null
  }

  const handleWithdraw = () => {
    if (confirm(t('cookies.withdrawConfirm', 'Are you sure you want to withdraw your consent? This will clear all non-essential cookies.'))) {
      withdrawCookieConsent()
      window.location.reload()
    }
  }

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <h3 className="font-medium text-gray-900 dark:text-white mb-2">
        {t('cookies.consentSettings', 'Cookie Consent Settings')}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {t('cookies.lastConsent', 'You last gave consent on')}:{' '}
        {consentState.timestamp ? new Date(consentState.timestamp).toLocaleDateString() : t('cookies.unknown', 'Unknown')}
      </p>
      <button
        onClick={handleWithdraw}
        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
      >
        {t('cookies.withdrawConsent', 'Withdraw Consent')}
      </button>
    </div>
  )
}
